/** @format */

import React, { useState, useEffect } from 'react';
import { Grid, Button, Stepper, Step, Typography, StepButton } from '@mui/material';
import { ButtonLoading, PageContainer } from 'components';
import { makeStyles } from '@mui/styles';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { IPs, PublicKey, Addresses } from './components';
import keysService from '../services/KeysService';
import MainForm from '../components/MainForm';
import CssBox from '../components/CssBox';
import createToast from 'components/Toast/Toast';
import { getDynamicObjectAction, constBreadCrumbsTitle } from 'utility';


const services = new keysService();

const isEnabledValue = (value) => {
  if (typeof value === 'string') {
    return ['true', '1', 'yes', 'enabled'].includes(value.trim().toLowerCase());
  }

  return value === true || value === 1;
};

const hasImplicitGrant = (data) => {
  if (isEnabledValue(data?.implicitAllowed) || isEnabledValue(data?.isImplicitAllowed)) {
    return true;
  }

  const grantTypes = data?.allowedGrantTypes ?? data?.grantTypes;
  const grants = Array.isArray(grantTypes) ? grantTypes : [grantTypes];

  return grants.some((grant) => {
    if (grant && typeof grant === 'object') {
      if (Object.prototype.hasOwnProperty.call(grant, 'implicit')) {
        return isEnabledValue(grant.implicit);
      }

      const grantValue =
        grant.value ?? grant.name ?? grant.code ?? grant.type ?? grant.grantType;

      return String(grantValue ?? '').trim().toLowerCase().includes('implicit');
    }

    return String(grant ?? '').trim().toLowerCase().includes('implicit');
  });
};

export default function KeysList({ keyId = null, ...props }) {
  // --STATES--//
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [skipped, setSkipped] = useState(new Set());

  const [clientId, setClientId] = useState(keyId);
  const [mainFormState, setMainFormState] = useState({
    name: '',
    url: '',
    supportNumber: '',
    appHash: '',
    description: '',
  });
  const [mainCheckBox, setMainCheckBox] = useState({
    implicitAllowed: false,
    captchaEnabled: false,
    signupEnabled: false,
    pkceEnabled: false,
    cssEnabled: false,
  });
  const [ipList, setIpList] = useState([]);
  const [addressesList, setAddressesList] = useState([]);
  const [isAddressListEmpty, setIsAddressListEmpty] = useState(false);
  const [css, setCss] = useState(null);
  const [logo, setLogo] = useState(null);
  const [hasPublicKey, setHasPublicKey] = useState(false);
  const [keyFormat, setKeyFormat] = useState('xml');
  const [publicKey, setPublicKey] = useState(null);

  // --HOOKS--//
  const classes = useStyles();
  const methods = useForm();
  const { handleSubmit, reset } = methods;
  const actionsList =
    getDynamicObjectAction([
      'contentAddKeyPage',
      'updateKeyListBtn'
    ]);

  const steps = getSteps();
  const isStepOptional = (step) => {
    return step === 2;
  };

  const isStepSkipped = (step) => {
    return skipped.has(step);
  };

  const handleNext = () => {
    if (
      mainFormState?.name.length === 0 ||
      mainFormState?.url.length === 0 ||
      (mainFormState?.supportNumber.length === 0 && activeStep === 0)
    ) {
      createToast('خطا', 'پر کردن فیلدهای ستاره دار اجباری است', 'danger');
    } else if (addressesList?.length === 0 && activeStep === 1) {
      createToast('خطا', 'لیست آدرس‌های پذیرنده نباید خالی باشد', 'danger');
    } else {
      let newSkipped = skipped;
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      setSkipped(newSkipped);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSkip = () => {
    if (!isStepOptional(activeStep)) {
      throw new Error('این مرحله اختیاری نیست . ');
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped((prevSkipped) => {
      const newSkipped = new Set(prevSkipped.values());
      newSkipped.add(activeStep);
      return newSkipped;
    });
  };

  const handleReset = () => {
    setActiveStep(0);
  };
  const handleStep = (step) => () => {
    setActiveStep(step);
  };
  function getSteps() {
    return ['اطلاعات کلی', 'آدرس های پذیرنده', 'لیست Ip ها', 'کلید عمومی', 'css'];
  }
  function getStepContent(step) {
    switch (step) {
      case 0:
        return (
          <MainForm
            methods={methods}
            logo={logo}
            setLogo={setLogo}
            classes={classes}
            state={mainFormState}
            setState={setMainFormState}
            checkboxGroup={mainCheckBox}
            setCheckBoxGroup={setMainCheckBox}
          />
        );
      case 1:
        return <Addresses items={addressesList} setItems={setAddressesList} />;
      case 2:
        return <IPs items={ipList} setItems={setIpList} />;
      case 3:
        return (
          <PublicKey
            setData={(d) => {
              setKeyFormat(d?.keyFormat);
              setPublicKey(d?.publicKey);
            }}
            data={{ keyFormat, publicKey }}
          />
        );
      case 4:
        return <CssBox css={css} setCss={setCss} />;
      default:
        return 'Unknown step';
    }
  }

  useEffect(() => {
    if (keyId) {
      getKey();
      getPublicKey(keyId);
      getCss(keyId);
    }
  }, [keyId]);

  useEffect(() => {
    if (addressesList?.length > 0) setIsAddressListEmpty(false);
  }, [addressesList]);

  // --API--//
  async function getKey() {
    try {
      setLoading(true);
      const result = await services.getKeyByClientId(clientId);
      const data = result.result;
      const implicitAllowed = hasImplicitGrant(data);
      setIpList(data?.allowedUserIPs);
      setAddressesList(data?.allowedRedirectUris);
      setLogo(data?.icon);
      setMainFormState({
        ...mainFormState,
        name: data?.name,
        url: data?.url,
        supportNumber: data?.supportNumber,
        appHash: data?.appHash,
        description: data?.description,
      });

      setMainCheckBox({
        signupEnabled: Boolean(data?.signupEnabled),
        pkceEnabled: Boolean(data?.pkceEnabled),
        captchaEnabled: Boolean(data?.captchaEnabled),
        appHash: data?.appHash,
        implicitAllowed,
        cssEnabled: Boolean(data?.cssEnabled),
      });
      const dataForm = {
        name: data?.name,
        description: data?.description || '',
        url: data?.url,
        supportNumber: data?.supportNumber || '',
        captchaEnabled: data?.captchaEnabled,
        signupEnabled: data?.signupEnabled,
        pkceEnabled: data?.pkceEnabled,
        appHash: data?.appHash,
        implicitAllowed,
      };

      reset(dataForm);
      setLoading(false);
    } catch (e) {
      console.error('getKey error:', e);
      setLoading(false);
    }
  }
  async function onSubmit(data) {
    if (addressesList?.length == 0) {
      createToast('خطا', 'لیست آدرس‌های پذیرنده نباید خالی باشد.', 'danger');

      setIsAddressListEmpty(true);
      return;
    }

    const dataApi = {
      name: mainFormState?.name,
      description: mainFormState?.description,
      url: encodeURI(mainFormState?.url),
      implicitAllowed: Boolean(data.implicitAllowed),
      captchaEnabled: Boolean(data.captchaEnabled),
      signupEnabled: mainCheckBox.signupEnabled,
      pkceEnabled: mainCheckBox.pkceEnabled,
      cssEnabled: mainCheckBox.cssEnabled,
      supportNumber: mainFormState?.supportNumber, // integer
      icon: logo ?? null,
      redirect_uri: addressesList,
      allowedUserIP: ipList,
      appHash: mainFormState?.appHash,
    };

    // return
    try {
      setIsSubmitting(true);
      const isPost = !clientId;
      const result = isPost
        ? await services.addKey(dataApi)
        : await services.editKey(clientId, dataApi);
      const savedClientId = result?.data?.result?.client_id || clientId;

      if (isPost) {
        setClientId(savedClientId);
        if (css) {
          let data = {
            styleCss: css,
          };
          const cssResult = await services.CreateCss(savedClientId, data);
          setCss(cssResult?.data?.result?.css);
        }
      } else {
        if (css) {
          let data = {
            styleCss: css,
          };
          const cssResult = await services.CreateCss(clientId, data);
          setCss(cssResult?.data?.result?.css);
        } else {
          let data = {
            styleCss: '',
          };
          const cssResult = await services.CreateCss(clientId, data);
          setCss(cssResult?.data?.result?.css);
        }
      }
      await handlePublicKeySubmission(savedClientId);

      setIsSubmitting(false);
      if (result.status === 200) {
        if (isPost) {
          navigate('/Keys');

          createToast('موفق', 'کلید جدید با موفقیت ایجاد شد', 'success');
        } else {
          createToast('موفق', 'کلید با موفقیت ویرایش شد', 'success');
          navigate('/Keys')
        }
      }
    } catch (error) {
      setIsSubmitting(false);
    }
  }

  /// public key methods

  const handlePublicKeySubmission = async (cId) => {
    const hasCompletePublicKey =
      typeof publicKey === 'string' &&
      publicKey.trim().length > 0 &&
      typeof keyFormat === 'string' &&
      keyFormat.trim().length > 0;

    if (hasPublicKey) {
      if (!hasCompletePublicKey) {
        await deletePublicKey(cId);
      } else {
        await putPublicKey(cId);
      }
    } else if (hasCompletePublicKey) {
      await postPublicKey(cId);
    }
  };

  async function getPublicKey(cId) {
    setHasPublicKey(false);
    setKeyFormat('xml');
    setPublicKey(null);

    try {
      const data = await services.getPublicKey(cId);
      const receivedKeyFormat = data?.result?.keyFormat;
      const receivedPublicKey = data?.result?.publicKey;
      const hasReceivedPublicKey =
        typeof receivedPublicKey === 'string' &&
        receivedPublicKey.trim().length > 0;
      const normalizedKeyFormat =
        typeof receivedKeyFormat === 'string' &&
        ['xml', 'pem'].includes(receivedKeyFormat.trim().toLowerCase())
          ? receivedKeyFormat.trim().toLowerCase()
          : 'xml';

      setKeyFormat(normalizedKeyFormat);

      if (hasReceivedPublicKey) {
        setPublicKey(receivedPublicKey);
        setHasPublicKey(true);
      }
    } catch (error) {
      console.log(error);
    }
  }

  async function postPublicKey(itemId) {
    if (hasPublicKey) return;
    try {
      await services.postPublicKey(itemId, {
        publicKey,
        keyFormat,
      });
      setHasPublicKey(true);
    } catch (error) {
      console.log(error);
    }
  }
  async function getCss(keyId) {
    try {
      const cssResult = await services.getCss(keyId);
      setCss(cssResult?.data?.result.css);
    } catch (err) {
      console.log(err);
    }
  }
  async function putPublicKey(itemId) {
    if (!hasPublicKey) return;
    try {
      await services.editPublicKey(itemId, {
        publicKey,
        keyFormat,
      });
    } catch (error) {
      console.log(error);
    }
  }

  const deletePublicKey = async (itemId) => {
    if (!hasPublicKey) return;
    try {
      await services.deletePublicKey(itemId);
      setHasPublicKey(false);
    } catch (error) {
      console.log(error);
    }
  };


  const hintText =
    'نقطه ورود هر کاربر به اپلیکیشن، کلید اتصال است. در این قسمت می‌توانید کلید اتصال جدیدی ایجاد نمایید و تنظیمات لازم مربوط به کلید اتصال خود را وارد نمایید.';

  const getKeyBreadCrumbsWithUrl = () => {
    return clientId ? constBreadCrumbsTitle.KEYS_EDIT_API_KEY : constBreadCrumbsTitle.KEYS__ADD_API_KEY
  }

  return (
    <>

      <PageContainer
        title={clientId ? 'ویرایش کلید' : 'افزودن کلید جدید'}
        description={hintText}
        className={classes.root}
        breadcrumbs={getKeyBreadCrumbsWithUrl()}
        loading={loading}
      >
        {(actionsList['contentAddKeyPage'] || actionsList['updateKeyListBtn']) && (
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} noValidate autoComplete="off">
              <Grid container>
                <Stepper activeStep={activeStep} nonLinear className={classes.keyStepper}>
                  {steps.map((label, index) => {
                    const stepProps = {};
                    const labelProps = {};
                    if (isStepOptional(index)) {
                      labelProps.optional = <Typography variant="caption">اختیاری</Typography>;
                    }
                    if (isStepSkipped(index)) {
                      stepProps.completed = false;
                    }
                    return (
                      <Step key={label} {...stepProps}>
                        <StepButton
                          {...labelProps}
                          onClick={clientId ? handleStep(index) : () => { }}
                        >
                          {label}
                        </StepButton>
                      </Step>
                    );
                  })}
                </Stepper>
                {activeStep === steps.length ? (
                  <div>
                    <Typography className={classes.instructions}>
                      All steps completed - you&apos;re finished
                    </Typography>
                    <Button onClick={handleReset} className={classes.button}>
                      Reset
                    </Button>
                  </div>
                ) : (
                  <div className={classes.stepperContentContainer}>
                    <Typography className={classes.instructions}>
                      {getStepContent(activeStep)}
                    </Typography>
                    <div className={classes.btnContainer}>
                      <Button disabled={activeStep === 0} onClick={handleBack} variant="contained">
                        بازگشت به مرحله قبل
                      </Button>
                      {isStepOptional(activeStep) && (
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleSkip}
                          className={classes.button}
                          style={{ marginLeft: 13 }}
                        >
                          رد کردن
                        </Button>
                      )}
                      {activeStep === steps.length - 1 ? (
                        <div>
                          <Button variant="contained" onClick={() => navigate('/Keys')}>
                            انصراف
                          </Button>
                          <ButtonLoading
                            type="submit"
                            variant="contained"
                            color="primary"
                            loading={isSubmitting}
                            name="ذخیره اطلاعات"
                          />

                        </div>
                      ) : (
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleNext}
                          className={classes.button}
                        >
                          مرحله بعد
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </Grid>
            </form>
          </FormProvider>
        )}
      </PageContainer>

    </>
  );
}

const useStyles = makeStyles((theme) => {
  return {
    root: {
      overflow: 'revert',
      '& .MuiCard-root': {
        overflow: 'unset',
      },
    },
    keyStepper: {
      alignItems: 'center',
      width: '100%',
      '& .MuiStepConnector-line': {
        width: ' 100%',
        height: '2px',
        background: '#A1AAE380',
        border: 0,
      },
      '& .MuiStepLabel-iconContainer': {
        '& .MuiStepIcon-root.Mui-active': {
          color: theme.palette.primary.main,
        },
        '& .MuiStepIcon-root': {
          width: '32px',
          height: '32px',
          borderRadius: '4px',
          border: ' 1px solid #A1AAE380',
          background: ' #A1AAE312',
          '&.Mui-active': {
            border: ' 1px solid #303d8f',
            background: '#303d8f',
            '& text': {
              fill: '#fff',
            }
          },
          '& text': {
            fill: '#000000de',
            fontSize: '11px',
          }

        },

        '& circle': {
          display: 'none',
        }

      },
      '& .MuiStepLabel-label.Mui-active': {
        color: '#303d8f ',
      },
      '& .MuiStepLabel-label': {
        fontSize: '14px',
        color: '#303d8f ',
        fontWeight: '500',
      },
      '& .MuiTypography-caption': {
        fontSize: '10px',
        color: '#303d8f',
        fontWeight: '500',
      },

    },
    stepperContentContainer: {
      width: '100%',
      padding: '24px',
    },
    keyStepper__step: {},
    keyStepper__label: {},
    keyStepper__content: {},
    submitionContainer: {},
    btnContainer: {
      marginTop: '30px',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      alignContent: 'flex-start',
      justifyContent: 'flex-end',
      '& button': {
        '&:first-child': {
          marginRight: '15px',
        }

      },
    },
    errors: {
      display: 'none',
    },
    uploader: {
      marginInlineStart: 'auto',
      marginInlineEnd: 0,
      [theme.breakpoints.down('lg')]: {
        marginInlineStart: 0,
        marginInlineEnd: 'auto',
        marginBottom: theme.spacing(2),
      },
    },
  };
});
